import {Vue} from 'vue-property-decorator'
import {EventBus} from '@/main'
import {Attachment} from '@/lib/kepler/interfaces'

export class CameraHelper {
  public Url: string | null = null
  public width: number | null = null
  public height: number | null = null
  public options: CameraOptions = {}
  public deferUpload: boolean = false
  private b64: string | null = null
  private Entry: FileEntry | null = null
  private readonly isBrowser: boolean = false
  private readonly platform: string = ''

  private readonly uploadUrl!: string
  private readonly uploadCallback!: (r: any) => void

  constructor(uploadUrl?: string, opts?: CameraOptions, deferUpload?: boolean, uploadCb?: (r: any) => void) {
    this.uploadUrl = uploadUrl || ''
    this.platform = device.platform.toLowerCase()
    this.isBrowser = device.platform === 'browser'
    if (opts) {
      Vue.set(this, 'options', opts)
      Vue.set(this, 'width', opts.targetWidth ? opts.targetWidth : null)
      Vue.set(this, 'height', opts.targetHeight ? opts.targetHeight : null)
    }
    if (deferUpload !== undefined) {
      Vue.set(this, 'deferUpload', deferUpload)
    }
    if (uploadCb) {
      this.uploadCallback = uploadCb
    }
  }

  public init() {
    const uploadIfNotDeferred = () => {
      if (!this.deferUpload) {
        this.uploadPicture().then((r: Attachment) => {
          this.uploadCallback(r)
        })
      }
    }
    if (this.isBrowser || this.platform === 'ios') {
      this.getPicture().then((b64) => {
        EventBus.$emit('gotPhoto')
        Vue.set(this, 'b64', b64)
        Vue.set(this, 'Url', URL.createObjectURL(this.getBlob(b64)))
        uploadIfNotDeferred()
      }).catch((e: string) => {
        console.error(e)
        EventBus.$emit('cameraError')
      })
    } else {
      // Normalize URL:
      const normalize = (f: string) => {
        return (window as any).Ionic.WebView.convertFileSrc(f)
      }

      // save picture in cache and get fileEntry if successful
      this.getPicture().then(async (uri) => {
        await this.getFileEntry(uri).then((e) => {
          Vue.set(this, 'Url', normalize(e.nativeURL))
          Vue.set(this, 'Entry', e)
          EventBus.$emit('gotPhoto')
          uploadIfNotDeferred()
        })
        // return await this.moveToCache(e, 'tempPicture-' + e.name).then((e) => {
        //   Vue.set(this, 'Url', e.toInternalURL())
        //   Vue.set(this, 'Entry', e)
        //   EventBus.$emit('gotPhoto')
        //   console.log('got photo, uri: ' + uri + ' --> ', e , e.toInternalURL())
        //   uploadIfNotDeferred()
        // })
      }).catch((e: string) => {
        console.error(e)
      })
    }
  }

  public cleanUp() {
    return new Promise((resolve, reject) => {
      navigator.camera.cleanup(resolve, reject)
    })
  }

  public manuallySetUrl(Url: string) {
    this.Url = Url
    Vue.set(this, 'Url', Url)
  }

  public setDimensions(w: number, h: number) {
    this.width = w
    this.height = h
  }

  public uploadPicture(blob?: Blob) {
    const actuallySendTheStuff = (resolve: (r: Attachment) => void, reject: () => void, b64?: string) => {
      // TODO: make this stuff happen with Axios / sdk
      if (!b64 && !blob) {
        reject()
      } else {
        const formData = new FormData()
        formData.append('file', blob ? blob : this.getBlob(b64!))
        const oReq = new XMLHttpRequest()
        oReq.open('POST', this.uploadUrl, true)
        oReq.onload = () => {
          if (oReq.status >= 200 && oReq.status < 400) {
            this.uploadCallback(JSON.parse(oReq.responseText))
            resolve(JSON.parse(oReq.responseText) as Attachment)
          } else {
            reject()
          }
        }
        oReq.onerror = () => {
          reject()
        }
        oReq.send(formData)
      }
    }

    return new Promise((resolve: (r: Attachment) => void, reject) => {
      if (blob) {
        actuallySendTheStuff(resolve, reject)
      } else {
        this.discriminateSource().then((b64) => {
          actuallySendTheStuff(resolve, reject, b64)
        }).catch(reject)
      }
    })
  }

  private getPicture() {
    if (this.isBrowser || this.platform === 'ios') {
      Vue.set(this.options, 'destinationType', Camera.DestinationType.DATA_URL)
      Vue.set(this.options, 'encodingType', Camera.EncodingType.JPEG)
    }
    return new Promise((resolve: (uri: string) => void, reject) => {
      navigator.camera.getPicture(resolve, reject, this.options)
      if (this.isBrowser) {
        this.styleBrowserCamera()
      }
    })
  }

  // browser stuff
  private styleBrowserCamera() {
    this.waitForElementToDisplay(() => {
      const camera = document.querySelector('.cordova-camera-capture')
      const cameraVideoElement = document.querySelector('.cordova-camera-capture video') as HTMLVideoElement | null
      const container = document.querySelector('.dummy-camera-box')
      const cameraButton = document.querySelector('.cordova-camera-capture button')

      if (!!container && !!camera) {
        container.appendChild(camera)
        if (cameraButton) {
          cameraButton.classList.add('component-button', 'v-btn', 'v-btn--depressed', 'v-btn--round')
        }
      }
      if (camera && cameraVideoElement) {
        Vue.nextTick(() => {
          Vue.set(this, 'width', cameraVideoElement.videoWidth || this.options.targetWidth)
          Vue.set(this, 'height', cameraVideoElement.videoHeight || this.options.targetHeight)
          Vue.set(this.options, 'targetWidth', cameraVideoElement.videoWidth)
          Vue.set(this.options, 'targetHeight', cameraVideoElement.videoHeight)
          EventBus.$emit('browserCameraLaunched')
        })
      }
    })
  }

  private waitForElementToDisplay(cb: () => any) {
    const el = document.querySelector('.cordova-camera-capture video') as HTMLVideoElement | null

    if (el !== null) {
      cb()
      return
    } else {
      setTimeout(() => {
        this.waitForElementToDisplay(cb)
      }, 100)
    }
  }

  // get fileEntry from uri
  private getFileEntry(uri: string) {
    return new Promise(function (resolve: (e: Entry) => void, reject) {
      window.resolveLocalFileSystemURL(uri, resolve, reject)
    })
  }

  private moveToCache(e: Entry, newName: string) {
    // gets cache Entry
    return this.getFileEntry(cordova.file.cacheDirectory).then((cacheEntry) => {
      return new Promise(function (resolve: (e: Entry) => void, reject) {
        e.moveTo(cacheEntry as DirectoryEntry, newName, resolve, reject)
      })
    })
  }

  // blob from base64
  private getBlob(b64Data: string, contentType?: string, sliceSize: number = 512) {
    let enc = ''
    switch (this.options.encodingType) {
      case 0:
        enc = 'image/JPEG'
        break
      case 1:
        enc = 'image/PNG'
    }
    contentType = contentType || enc

    const byteCharacters = atob(b64Data)
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)
      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }
    return new Blob(byteArrays, {type: contentType})
  }


  private getFileFromFileEntry(fileEntry: FileEntry) {
    return new Promise(function (resolve: (file: File) => void, reject?: (error: FileError) => void) {
      fileEntry.file(resolve, reject)
    })
  }

  private readFile(fileEntry: FileEntry) {
    return this.getFileFromFileEntry(fileEntry).then((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onerror = (event) => {
            console.error('File could not be read!')
            reject(event)
          }
          reader.onload = (e: any) => {
            resolve(e.target.result.replace(/data:.*\/.*;base64,/gm, '') as string)
          }
          reader.readAsDataURL(file)
        })
      },
    )
  }

  private getString(entry: FileEntry) {
    return this.readFile(entry).then((f) => {
      return f as string
    })
  }

  private discriminateSource() {
    return new Promise((resolve: (x: string) => void, reject) => {
      if ((this.isBrowser || this.platform === 'ios') && this.b64) {
        resolve(this.b64)
      } else if (this.Entry) {
        this.getString(this.Entry).then((r) => {
            resolve(r)
          },
        )
      } else {
        reject()
      }
    })
  }

}
